import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { tx, useLang } from "../lib/i18n";

export const DISCOVER = [
  ["for-you", "For You", "Sparkles"], ["new-in", "New In", "Sparkle"], ["sale", "Sale", "Tag"], ["flash-deals", "Flash Deals", "Zap"],
  ["best-sellers", "Best Sellers", "Star"], ["trending", "Trending", "TrendingUp"], ["mivo-exclusive", "MIVO Exclusive", "Gem"],
  ["bulk-deals", "Bulk Deals", "Package"], ["recommended", "Recommended Products", "Heart"], ["recently-viewed", "Recently Viewed", "History"],
];
const cache = {};

export function useCategories() {
  const lang = useLang();
  const [cats, setCats] = useState(cache[lang] || []);
  useEffect(() => {
    if (cache[lang]) return setCats(cache[lang]);
    api.get("/categories").then((r) => { cache[lang] = r.data; setCats(r.data); });
  }, [lang]);
  return useMemo(() => {
    const mains = cats.filter((c) => !c.parent_id);
    return mains.map((m) => ({ ...m, subs: cats.filter((c) => c.parent_id === m.category_id) }));
  }, [cats]);
}
export const clearCategoryCache = () => Object.keys(cache).forEach((k) => delete cache[k]);

const Ico = ({ name, className }) => { const I = Icons[name] || Icons.LayoutGrid; return <I className={className} />; };
const label = (c) => c.display_name || c.name;
const match = (c, q) => label(c).toLowerCase().includes(q) || c.name.toLowerCase().includes(q);

function useFiltered(tree, q) {
  return useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return null;
    return tree.map((m) => ({ ...m, hit: match(m, s), subs: m.subs.filter((c) => match(c, s)) })).filter((m) => m.hit || m.subs.length);
  }, [tree, q]);
}

export function MegaMenu() {
  useLang();
  const tree = useCategories();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const loc = useLocation();
  const filtered = useFiltered(tree, q);
  useEffect(() => setOpen(false), [loc.pathname, loc.search]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onClick = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    window.addEventListener("keydown", onKey); window.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mousedown", onClick); };
  }, [open]);
  const cur = tree.find((m) => m.category_id === active) || tree[0];
  const current = new URLSearchParams(loc.search).get("category");
  return (
    <div ref={ref} className="hidden lg:block">
      <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid="header-categories-button"
        className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${open ? "border-[var(--mv-teal)] text-[var(--mv-teal)]" : "border-[var(--mv-border-2)] hover:border-[var(--mv-teal)]"}`}>
        <Icons.LayoutGrid className="h-4 w-4" /> {tx("Categories")} <Icons.ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute inset-x-0 top-full z-50 border-b border-[var(--mv-border-2)] bg-[#070C0E] shadow-2xl backdrop-blur-xl mv-fade-in" data-testid="mega-menu">
          <div className="mv-container grid grid-cols-[300px_1fr] gap-8 py-6">
            <div className="flex max-h-[70vh] flex-col">
              <div className="relative mb-3">
                <Icons.Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--mv-dim)]" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tx("Search categories")} autoFocus
                  className="w-full rounded-xl border border-[var(--mv-border-2)] bg-[var(--mv-card)] py-2.5 pl-9 pr-3 text-[15px] outline-none focus:border-[var(--mv-teal)]" data-testid="mega-menu-search" />
              </div>
              <ul className="mv-scroll -mr-2 flex-1 overflow-y-auto pr-2" data-testid="mega-menu-main-list">
                {tree.map((m) => (
                  <li key={m.slug}>
                    <Link to={`/shop?category=${m.slug}`} onMouseEnter={() => setActive(m.category_id)} onFocus={() => setActive(m.category_id)} data-testid={`mega-main-${m.slug}`}
                      className={`flex min-h-[44px] items-center gap-3 rounded-xl px-3 text-[15px] font-semibold transition-colors ${cur?.slug === m.slug ? "bg-[var(--mv-teal-dim)] text-[var(--mv-teal)]" : "hover:bg-white/5"} ${current === m.slug ? "ring-1 ring-[var(--mv-teal)]" : ""}`}>
                      <Ico name={m.icon} className="h-[18px] w-[18px] shrink-0" /><span className="flex-1">{label(m)}</span>
                      {m.count > 0 && <span className="text-xs font-normal text-[var(--mv-dim)]">{m.count}</span>}<Icons.ChevronRight className="h-4 w-4 opacity-50" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="mv-scroll max-h-[70vh] overflow-y-auto">
              {filtered ? <SearchResults groups={filtered} /> : cur && <MainPane m={cur} current={current} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MainPane({ m, current }) {
  useLang();
  return (
    <div data-testid="mega-menu-pane">
      <div className="mb-5 flex items-end justify-between gap-4 border-b border-[var(--mv-border)] pb-4">
        <div className="flex items-center gap-3"><Ico name={m.icon} className="h-6 w-6 text-[var(--mv-teal)]" /><h3 className="text-xl font-semibold">{label(m)}</h3></div>
        <Link to={`/shop?category=${m.slug}`} className="text-sm text-[var(--mv-teal)]" data-testid="mega-view-all">{tx("View all")} →</Link>
      </div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-1 xl:grid-cols-3 2xl:grid-cols-4">
        {m.subs.map((c) => (
          <Link key={c.slug} to={`/shop?category=${c.slug}`} data-testid={`mega-sub-${c.slug}`}
            className={`flex min-h-[40px] items-center justify-between rounded-lg px-2 text-[14px] transition-colors hover:bg-white/5 hover:text-[var(--mv-teal)] ${current === c.slug ? "text-[var(--mv-teal)]" : "text-[var(--mv-muted)]"}`}>
            <span>{label(c)}</span>{c.count > 0 && <span className="text-xs text-[var(--mv-dim)]">{c.count}</span>}
          </Link>
        ))}
      </div>
      {m.regulated && <p className="mt-4 text-xs text-[var(--mv-dim)]">{tx("Regulated category — listings are reviewed for applicable requirements before publication.")}</p>}
      <p className="mb-2 mt-7 text-xs font-semibold tracking-[0.14em] text-[var(--mv-teal)]">{tx("SHOP COLLECTIONS")}</p>
      <div className="flex flex-wrap gap-2">
        {DISCOVER.map(([k, l, i]) => (
          <Link key={k} to={`/discover/${k}`} className="flex items-center gap-1.5 rounded-full border border-[var(--mv-border-2)] px-3 py-1.5 text-[13px] hover:border-[var(--mv-teal)] hover:text-[var(--mv-teal)]" data-testid={`mega-discover-${k}`}>
            <Ico name={i} className="h-3.5 w-3.5" />{tx(l)}
          </Link>
        ))}
      </div>
    </div>
  );
}

function SearchResults({ groups, onPick }) {
  useLang();
  if (!groups.length) return <p className="text-sm text-[var(--mv-muted)]" data-testid="category-search-empty">{tx("No matching categories")}</p>;
  return (
    <div className="space-y-4" data-testid="category-search-results">
      {groups.map((m) => (
        <div key={m.slug}>
          <Link to={`/shop?category=${m.slug}`} onClick={onPick} className="flex min-h-[44px] items-center gap-2 text-[15px] font-semibold hover:text-[var(--mv-teal)]"><Ico name={m.icon} className="h-4 w-4" />{label(m)}</Link>
          <div className="flex flex-wrap gap-2">
            {m.subs.map((c) => <Link key={c.slug} to={`/shop?category=${c.slug}`} onClick={onPick} className="rounded-full border border-[var(--mv-border-2)] px-3 py-2 text-[14px] text-[var(--mv-muted)] hover:text-[var(--mv-teal)]">{label(c)}</Link>)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function MobileCategoryBrowser({ onClose, nav }) {
  useLang();
  const tree = useCategories();
  const [sel, setSel] = useState(null);
  const [q, setQ] = useState("");
  const filtered = useFiltered(tree, q);
  const go = useNavigate();
  const pick = (to) => { onClose(); go(to); };
  const m = tree.find((x) => x.slug === sel);
  const row = "flex min-h-[48px] w-full items-center gap-3 rounded-xl px-3 text-left text-base transition-colors hover:bg-white/5";
  return (
    <div className="flex h-full flex-col" data-testid="mobile-category-drawer">
      <div className="flex min-h-[56px] items-center gap-2 border-b border-[var(--mv-border)] px-2">
        {m ? <button onClick={() => setSel(null)} className="grid h-11 w-11 place-items-center rounded-full hover:bg-white/5" aria-label={tx("Back")} data-testid="mobile-category-back"><Icons.ChevronLeft className="h-5 w-5" /></button> : <span className="w-2" />}
        <p className="flex-1 text-base font-semibold">{m ? label(m) : tx("Menu")}</p>
        <button onClick={onClose} className="grid h-11 w-11 place-items-center rounded-full hover:bg-white/5" aria-label={tx("Close")} data-testid="mobile-category-close"><Icons.X className="h-5 w-5" /></button>
      </div>
      <div className="px-4 pt-3">
        <div className="relative">
          <Icons.Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--mv-dim)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tx("Search categories")} className="h-12 w-full rounded-xl border border-[var(--mv-border-2)] bg-[var(--mv-card)] pl-9 pr-3 text-base outline-none focus:border-[var(--mv-teal)]" data-testid="mobile-category-search" />
        </div>
      </div>
      <div className="mv-scroll flex-1 overflow-y-auto px-2 pb-8 pt-2">
        {filtered ? <div className="px-2 pt-2"><SearchResults groups={filtered} onPick={onClose} /></div> : m ? (
          <ul data-testid="mobile-subcategory-list">
            <li><button onClick={() => pick(`/shop?category=${m.slug}`)} className={`${row} font-semibold text-[var(--mv-teal)]`} data-testid="mobile-category-all">{tx("View all")} · {label(m)}</button></li>
            {m.subs.map((c) => <li key={c.slug}><button onClick={() => pick(`/shop?category=${c.slug}`)} className={row} data-testid={`mobile-sub-${c.slug}`}><span className="flex-1">{label(c)}</span>{c.count > 0 && <span className="text-xs text-[var(--mv-dim)]">{c.count}</span>}</button></li>)}
          </ul>
        ) : (
          <>
            {nav}
            <p className="px-3 pb-1 pt-5 text-xs font-semibold tracking-[0.14em] text-[var(--mv-teal)]">{tx("SHOP BY CATEGORY")}</p>
            <ul data-testid="mobile-main-category-list">
              {tree.map((c) => (
                <li key={c.slug}><button onClick={() => setSel(c.slug)} className={`${row} font-medium`} data-testid={`mobile-main-${c.slug}`}>
                  <Ico name={c.icon} className="h-5 w-5 shrink-0 text-[var(--mv-teal)]" /><span className="flex-1">{label(c)}</span><Icons.ChevronRight className="h-5 w-5 opacity-50" />
                </button></li>
              ))}
            </ul>
            <p className="px-3 pb-1 pt-5 text-xs font-semibold tracking-[0.14em] text-[var(--mv-teal)]">{tx("SHOP COLLECTIONS")}</p>
            <ul>{DISCOVER.map(([k, l, i]) => <li key={k}><button onClick={() => pick(`/discover/${k}`)} className={row} data-testid={`mobile-discover-${k}`}><Ico name={i} className="h-5 w-5 shrink-0" />{tx(l)}</button></li>)}</ul>
          </>
        )}
      </div>
    </div>
  );
}

export function CategoryTitle({ slug }) {
  const { lang } = useApp();
  const tree = useCategories();
  const all = tree.flatMap((m) => [m, ...m.subs]);
  const c = all.find((x) => x.slug === slug);
  return c ? <span data-lang={lang}>{label(c)}</span> : null;
}
